import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Plus, Trash2, ShoppingCart, Info, Download, FileSpreadsheet } from 'lucide-react';
import { INSTRUMENTS_QPCR, INSTRUMENTS_DPCR, SPECIES, MIRNA_CONTROLS, REFERENCE_GENES, calculatePrice } from '@/utils/workflowConfig';
import * as XLSX from 'xlsx';

// Application info text
const APP_INFO = {
  realtime_pcr: {
    gene_expression: "If 'Hydrolysis Probe' is selected, 2 primers (forward and reverse) and 1 Hydrolysis probe will be designed for each gene region. If you select the multiplex option, probes will be labeled for different channels according to your selection and instrument. The design will be shared with you for confirmation.\n\nIf 'SYBR Green' is selected, only 2 primers (forward and reverse) will be designed for each gene region.\n\nIf the 'Exon spanning' option is selected, the design will be made at exon-exon junctions or to span an intron. This ensures the assay targets only RNA, and results will not be affected by potential DNA contamination.",
    genotyping: "It is possible to work with 4 different methods for mutation analysis via Real-Time PCR. For detailed information on each, you can contact us or review this link.",
    genotyping_taqman: "A design will be made for mutation detection via end-point analysis. A custom FAM-labeled, mutation-specific hydrolysis probe will be designed for the mutation. A custom HEX-labeled, wild-type allele-specific hydrolysis probe will also be designed.",
    genotyping_fret: "Mutation-specific Hybridization probes consist of 2 probes: 1 Anchor probe and 1 mutation-specific probe. These probes are used for mutation detection via melting analysis. If your instrument is a LightCycler 480 or LightCycler Pro, you can use these probes for multiplexing, allowing you to run 3-4 different mutations in a single well for the same sample. You can also combine SimpleMT Probes for 5-plex capability. Note: Multiplex analysis requires optimization, and your instrument must have color compensation capabilities.",
    genotyping_simplemt: "Mutation-specific SimpleMT probes consist of 1 probe and are used for mutation detection via melting analysis. You can combine SimpleMT Probes for 5-plex capability. Note: Multiplex analysis requires optimization, and your instrument must have color compensation capabilities.",
    genotyping_gace: "GACE Mutation Assays use the Allelic Competitive Extension method for genotyping. It uses 3 primers and 2 probes. With this design, only the primers will be sent to you. It must be used in conjunction with the GACE Probes master mix.",
    mirna: "miRNA assays can be designed for mature miRNAs for human, mouse, and rat. It's possible to work with either SYBR Green or a FAM-labeled hydrolysis probe (the probe used in miRNA assays is specially developed for these assays). You can access miRNA sequences at: https://www.mirbase.org/search/ or https://mirdb.org/.\n\nFor each miRNA, 3 primers are produced: 1 cDNA primer, 1 forward primer, and 1 reverse primer. If the probe option is selected, 1 probe is also designed.",
    pathogen_detection: "In this section, we can create singleplex or multiplex designs for organisms you wish to detect via Real-Time PCR (e.g., viruses, bacteria, fungi). If you select the multiplex option, probes will be labeled for different channels according to your selection and instrument. The design will be shared with you for confirmation. You can also use this for quantification; standard controls can be added during the design phase if required.",
    probe_ready: "This application was developed as an analogue to the 'Assay Design Center,' which was used for designing with the Universal Probe Library (UPL) products that were discontinued by their manufacturer at the end of 2020. You can either design assays to use with your existing UPL products or order ready-to-use 100 rxn or 500 rxn assays that work with specially developed probes based on a similar logic. All assays will be sent as 20X ready-to-use mixes containing 2 primers and 1 FAM-labeled probe.",
    gace_mas: "GACE MAS Marker Assays use the Allelic Competitive Extension method for genotyping. It uses 3 primers and 2 probes. With this design, only the primers will be sent to you. It must be used in conjunction with the GACE Probes master mix."
  },
  digital_pcr: {
    gene_expression: "If 'Hydrolysis Probe' is selected, 2 primers (forward and reverse) and 1 Hydrolysis probe will be designed for each gene region. If you select the multiplex option, probes will be labeled for different channels according to your selection and instrument. The design will be shared with you for confirmation.\n\nIf the 'Exon spanning' option is selected, the design will be made at exon-exon junctions or to span an intron. This ensures the assay targets only RNA, and results will not be affected by potential DNA contamination.",
    genotyping: "A design will be made for mutation detection via end-point analysis. A custom FAM-labeled, mutation-specific hydrolysis probe will be designed for the mutation. A custom HEX-labeled, wild-type allele-specific hydrolysis probe will also be designed.",
    mirna: "miRNA assays can be designed for mature miRNAs for human, mouse, and rat. It's possible to work with either SYBR Green or a FAM-labeled hydrolysis probe (the probe used in miRNA assays is specially developed for these assays). You can access miRNA sequences at: https://www.mirbase.org/search/ or https://mirdb.org/.\n\nFor each miRNA, 3 primers are produced: 1 cDNA primer, 1 forward primer, and 1 reverse primer. If the probe option is selected, 1 probe is also designed.\n\nIf you select the multiplex option, probes will be labeled for different channels according to your selection and instrument. The design will be shared with you for confirmation.",
    pathogen_detection: "In this section, we can create singleplex or multiplex designs for organisms you wish to detect via Real-Time PCR (e.g., viruses, bacteria, fungi). If you select the multiplex option, probes will be labeled for different channels according to your selection and instrument. The design will be shared with you for confirmation. You can also use this for quantification; standard controls can be added during the design phase if required."
  }
};

const WorkflowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToCart } = useCart();

  // Main workflow state
  const [technology, setTechnology] = useState('');
  const [application, setApplication] = useState('');
  const [step, setStep] = useState('technology');

  // Handle navigation state (from navbar dropdown)
  useEffect(() => {
    if (location.state?.technology) {
      setTechnology(location.state.technology);
      setStep('application');
    }
  }, [location.state]);

  // Configuration state
  const [config, setConfig] = useState({
    instrument: '',
    assay_type: '',
    multiplex: false,
    multiplex_level: '',
    species: '',
    exon_spanning: false,
    positive_control: false,
    quantification_control: false,
    synthetic_control: false,
    reference_gene: '',
    reference_genes: [],
    items: [{ name: '', sequence: '', comment: '' }],
    genotype_type: ''
  });

  const [calculated_price, setCalculatedPrice] = useState(0);
  const [designServiceOnly, setDesignServiceOnly] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Template URLs based on application type
  const TEMPLATE_URLS = {
    gene_expression: '/templates/gene_expression_upload_form.xlsx',
    mirna: '/templates/mirna_upload_form.xlsx',
    genotyping: '/templates/genotyping_upload_form.xlsx'
  };

  // Parse Excel file and extract data
  const parseExcelFile = async (file, applicationType) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          
          // Skip header row(s) and extract data
          const items = [];
          let startRow = 1; // Start from row 1 (after headers)
          
          for (let i = startRow; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;
            
            let name = '';
            let sequence = '';
            
            if (applicationType === 'gene_expression') {
              // Gene name | Sequence info (5'-3')
              name = row[0] ? String(row[0]).trim() : '';
              sequence = row[1] ? String(row[1]).trim() : '';
            } else if (applicationType === 'mirna') {
              // miRNA Name | Sequence info (5'-3')
              name = row[0] ? String(row[0]).trim() : '';
              sequence = row[1] ? String(row[1]).trim() : '';
            } else if (applicationType === 'genotyping') {
              // Mutation Region Name/rs number | Sequence info with mutation
              name = row[0] ? String(row[0]).trim() : '';
              sequence = row[1] ? String(row[1]).trim() : '';
            }
            
            // Only add if name is not empty and doesn't look like a header
            if (name && !name.toLowerCase().includes('name') && !name.toLowerCase().includes('form')) {
              items.push({ name, sequence, comment: '' });
            }
          }
          
          resolve(items);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file extension
    const extension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(extension)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploadedFile(file);
    toast.success(`File "${file.name}" selected. Click "Add Uploaded Data" to import.`);
  };

  // Add uploaded data to items
  const handleAddUploadedData = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      const parsedItems = await parseExcelFile(uploadedFile, application);
      
      if (parsedItems.length === 0) {
        toast.error('No valid data found in the file');
        return;
      }

      // Check for duplicates
      const existingNames = new Set(config.items.map(item => item.name.toLowerCase()).filter(n => n));
      const newItems = [];
      const duplicates = [];
      
      for (const item of parsedItems) {
        if (existingNames.has(item.name.toLowerCase())) {
          duplicates.push(item.name);
        } else {
          newItems.push(item);
          existingNames.add(item.name.toLowerCase());
        }
      }

      // Remove empty placeholder items
      const currentItems = config.items.filter(item => item.name.trim() !== '');
      
      // Combine with new items
      const allItems = [...currentItems, ...newItems];
      
      // If no items, keep at least one empty row
      if (allItems.length === 0) {
        allItems.push({ name: '', sequence: '', comment: '' });
      }

      setConfig({ ...config, items: allItems });
      setUploadedFile(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      const itemType = application === 'gene_expression' ? 'gene(s)' : 
                       application === 'mirna' ? 'miRNA(s)' : 'mutation(s)';
      
      if (duplicates.length > 0) {
        toast.warning(`Added ${newItems.length} ${itemType}. ${duplicates.length} duplicate(s) skipped.`);
      } else {
        toast.success(`Successfully added ${newItems.length} ${itemType} from Excel file!`);
      }
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      toast.error('Failed to parse Excel file. Please check the format.');
    }
  };

  // Download template
  const downloadTemplate = () => {
    const templateUrl = TEMPLATE_URLS[application];
    if (!templateUrl) {
      toast.error('No template available for this application type');
      return;
    }
    
    const link = document.createElement('a');
    link.href = templateUrl;
    const fileName = application === 'gene_expression' ? 'Gene_Expression_Upload_Form.xlsx' :
                     application === 'mirna' ? 'miRNA_Upload_Form.xlsx' :
                     'Genotyping_Upload_Form.xlsx';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded!');
  };

  const handleTechnologySelect = (tech) => {
    setTechnology(tech);
    setStep('application');
    setApplication('');
    setConfig({
      instrument: '',
      assay_type: '',
      multiplex: false,
      multiplex_level: '',
      species: '',
      exon_spanning: false,
      positive_control: false,
      quantification_control: false,
      synthetic_control: false,
      reference_gene: '',
      items: [{ name: '', sequence: '', comment: '' }],
      genotype_type: ''
    });
  };

  const handleApplicationSelect = (app) => {
    // Navigate directly for special applications
    if (app === 'custom_probes') {
      navigate('/custom-probes-primers');
      return;
    }
    if (app === 'probe_ready') {
      navigate('/probe-ready-design');
      return;
    }
    if (app === 'primer_design_wizard') {
      navigate('/primer-design-wizard');
      return;
    }
    
    setApplication(app);
    setStep('configuration');
  };

  const addItem = () => {
    setConfig(prev => ({
      ...prev,
      items: [...prev.items, { name: '', sequence: '', comment: '' }]
    }));
  };

  const removeItem = (index) => {
    setConfig(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleCalculate = () => {
    let price;
    if (designServiceOnly) {
      // Design service: €150 per item
      const numItems = config.items?.length || 1;
      price = numItems * 150;
    } else {
      price = calculatePrice(technology, application, config);
    }
    setCalculatedPrice(price);
    toast.success(`Price calculated: €${price.toFixed(2)}`);
  };

  const handleAddToCart = async () => {
    if (calculated_price === 0) {
      toast.error('Please calculate price first');
      return;
    }

    try {
      await addToCart({
        technology,
        application,
        configuration: {
          ...config,
          design_service_only: designServiceOnly
        },
        price: calculated_price,
        quantity: 1
      });
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleOrderNow = async () => {
    await handleAddToCart();
    navigate('/cart');
  };

  const renderApplicationButtons = () => {
    const qpcrApps = [
      { id: 'gene_expression', label: 'Gene Expression' },
      { id: 'genotyping', label: 'Genotyping' },
      { id: 'pathogen_detection', label: 'Pathogen Detection' },
      { id: 'mirna', label: 'miRNA Assays' },
      { id: 'primer_design_wizard', label: 'PrimerDesignWizard (Custom Primer/Probe Design)' },
      { id: 'custom_probes', label: 'Custom Order Your Probes & Primers' },
      { id: 'probe_ready', label: 'Design Your Own Probe Ready Assays' },
      { id: 'gace_mas', label: 'GACE MAS Marker Assay' }
    ];

    const dpcrApps = [
      { id: 'gene_expression', label: 'Gene Expression with Absolute Quantification' },
      { id: 'pathogen_detection', label: 'Pathogen Detection and Quantification' },
      { id: 'genotyping', label: 'Genotyping' },
      { id: 'mirna', label: 'miRNA Assays' },
      { id: 'primer_design_wizard', label: 'PrimerDesignWizard (Custom Primer/Probe Design)' },
      { id: 'custom_probes', label: 'Custom Order Your Probes & Primers' }
    ];

    const apps = technology === 'realtime_pcr' ? qpcrApps : dpcrApps;

    return (
      <div className="grid md:grid-cols-2 gap-4">
        {apps.map(app => (
          <button
            key={app.id}
            onClick={() => handleApplicationSelect(app.id)}
            className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all text-left"
            data-testid={`app-${app.id}`}
          >
            <h3 className="font-semibold text-gray-900">{app.label}</h3>
          </button>
        ))}
      </div>
    );
  };

  const renderGeneExpressionForm = () => {
    const infoKey = technology === 'realtime_pcr' ? 'realtime_pcr' : 'digital_pcr';
    const infoText = APP_INFO[infoKey]?.gene_expression;
    
    return (
    <div className="space-y-6">
      {/* Info Alert */}
      {infoText && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
            {infoText}
          </AlertDescription>
        </Alert>
      )}

      {/* Design Service Only Checkbox */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="design-service-only"
            checked={designServiceOnly}
            onCheckedChange={setDesignServiceOnly}
            data-testid="design-service-checkbox"
          />
          <div>
            <label htmlFor="design-service-only" className="font-medium text-gray-900 cursor-pointer">
              I want to purchase the Design Service only
            </label>
            <p className="text-sm text-gray-600 mt-1">
              Only the design will be completed and shared with you. Each design will be billed at €150.
            </p>
          </div>
        </div>
      </div>

      <div>
        <Label>Instrument</Label>
        <Select value={config.instrument} onValueChange={(val) => setConfig({...config, instrument: val})}>
          <SelectTrigger data-testid="instrument-select">
            <SelectValue placeholder="Choose your instrument" />
          </SelectTrigger>
          <SelectContent>
            {(technology === 'realtime_pcr' ? INSTRUMENTS_QPCR : INSTRUMENTS_DPCR).map(inst => (
              <SelectItem key={inst} value={inst}>{inst}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {technology === 'realtime_pcr' && (
        <div>
          <Label>Assay Type</Label>
          <div className="flex gap-4 mt-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sybr"
                checked={config.assay_type === 'sybr_green'}
                onCheckedChange={(checked) => setConfig({...config, assay_type: checked ? 'sybr_green' : ''})}
                data-testid="sybr-checkbox"
              />
              <label htmlFor="sybr">SYBR Green</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hydrolysis"
                checked={config.assay_type === 'hydrolysis_probe'}
                onCheckedChange={(checked) => setConfig({...config, assay_type: checked ? 'hydrolysis_probe' : ''})}
                data-testid="hydrolysis-checkbox"
              />
              <label htmlFor="hydrolysis">Hydrolysis Probe</label>
            </div>
          </div>
        </div>
      )}

      {(config.assay_type === 'hydrolysis_probe' || technology === 'digital_pcr') && (
        <div>
          <Label>Multiplex</Label>
          <RadioGroup value={config.multiplex ? 'yes' : 'no'} onValueChange={(val) => setConfig({...config, multiplex: val === 'yes'})}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="singleplex" />
              <Label htmlFor="singleplex">SinglePlex</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="multiplex" />
              <Label htmlFor="multiplex">Multiplex</Label>
            </div>
          </RadioGroup>
          {config.multiplex && (
            <Select value={config.multiplex_level} onValueChange={(val) => setConfig({...config, multiplex_level: val})}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select multiplex level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2plex">2plex</SelectItem>
                <SelectItem value="3plex">3plex</SelectItem>
                <SelectItem value="4plex">4plex</SelectItem>
                <SelectItem value="5plex">5plex</SelectItem>
                <SelectItem value="6plex">6plex</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox
          id="exon_spanning"
          checked={config.exon_spanning}
          onCheckedChange={(checked) => setConfig({...config, exon_spanning: checked})}
        />
        <label htmlFor="exon_spanning">Exon-spanning (if possible)</label>
      </div>

      <div>
        <Label>Species</Label>
        <Select value={config.species} onValueChange={(val) => setConfig({...config, species: val})}>
          <SelectTrigger data-testid="species-select">
            <SelectValue placeholder="Select species" />
          </SelectTrigger>
          <SelectContent>
            {SPECIES.map(sp => (
              <SelectItem key={sp} value={sp.toLowerCase()}>{sp}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reference Genes Selection */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <Label className="text-base font-semibold mb-3 block">Reference Genes (Optional)</Label>
        <p className="text-sm text-gray-600 mb-3">
          Select reference genes for normalization. Each reference gene adds €250 to the total price.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {REFERENCE_GENES.map((gene) => (
            <div key={gene} className="flex items-center space-x-2">
              <Checkbox
                id={`ref-gene-${gene}`}
                checked={config.reference_genes?.includes(gene)}
                onCheckedChange={(checked) => {
                  const current = config.reference_genes || [];
                  if (checked) {
                    setConfig({...config, reference_genes: [...current, gene]});
                  } else {
                    setConfig({...config, reference_genes: current.filter(g => g !== gene)});
                  }
                }}
                data-testid={`ref-gene-${gene}`}
              />
              <label htmlFor={`ref-gene-${gene}`} className="text-sm cursor-pointer">
                {gene}
              </label>
            </div>
          ))}
        </div>
        {config.reference_genes && config.reference_genes.length > 0 && (
          <div className="mt-3 text-sm font-medium text-blue-700">
            Selected: {config.reference_genes.length} reference gene(s) (+€{config.reference_genes.length * 250})
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>Gene Information</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add Gene
          </Button>
        </div>
        {config.items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-gray-50 rounded">
            <Input
              placeholder="Gene name"
              value={item.name}
              onChange={(e) => updateItem(idx, 'name', e.target.value)}
              className="col-span-4"
              data-testid={`gene-name-${idx}`}
            />
            <Input
              placeholder="Sequence (5' to 3')"
              value={item.sequence}
              onChange={(e) => updateItem(idx, 'sequence', e.target.value)}
              className="col-span-7"
              data-testid={`gene-sequence-${idx}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              className="col-span-1"
              disabled={config.items.length === 1}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Excel Template Upload Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-5 h-5 text-green-600" />
          <Label className="text-base font-semibold text-green-800">Or Upload Excel File</Label>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            className="border-green-300 text-green-700 hover:bg-green-100"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          
          <div className="flex-1 flex gap-2">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="flex-1"
              data-testid="excel-upload-gene"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleAddUploadedData}
              disabled={!uploadedFile}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-uploaded-data-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Uploaded Data
            </Button>
          </div>
        </div>
        
        {uploadedFile && (
          <p className="text-sm text-green-600 mt-2">
            File selected: {uploadedFile.name}
          </p>
        )}
        
        <p className="text-xs text-gray-500 mt-2">
          Download the template, fill in your gene names and sequences, then upload it here.
        </p>
      </div>
    </div>
    );
  };

  const renderPathogenDetectionForm = () => {
    const infoKey = technology === 'realtime_pcr' ? 'realtime_pcr' : 'digital_pcr';
    const infoText = APP_INFO[infoKey]?.pathogen_detection;
    
    return (
    <div className="space-y-6">
      {/* Info Alert */}
      {infoText && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
            {infoText}
          </AlertDescription>
        </Alert>
      )}

      {/* Design Service Only Checkbox */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="design-service-pathogen"
            checked={designServiceOnly}
            onCheckedChange={setDesignServiceOnly}
          />
          <div>
            <label htmlFor="design-service-pathogen" className="font-medium text-gray-900 cursor-pointer">
              I want to purchase the Design Service only
            </label>
            <p className="text-sm text-gray-600 mt-1">
              Only the design will be completed and shared with you. Each design will be billed at €150.
            </p>
          </div>
        </div>
      </div>

      <div>
        <Label>Instrument</Label>
        <Select value={config.instrument} onValueChange={(val) => setConfig({...config, instrument: val})}>
          <SelectTrigger data-testid="instrument-select">
            <SelectValue placeholder="Choose your instrument" />
          </SelectTrigger>
          <SelectContent>
            {(technology === 'realtime_pcr' ? INSTRUMENTS_QPCR : INSTRUMENTS_DPCR).map(inst => (
              <SelectItem key={inst} value={inst}>{inst}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Multiplex</Label>
        <RadioGroup value={config.multiplex ? 'yes' : 'no'} onValueChange={(val) => setConfig({...config, multiplex: val === 'yes'})}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="singleplex" />
            <Label htmlFor="singleplex">SinglePlex</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="multiplex" />
            <Label htmlFor="multiplex">Multiplex</Label>
          </div>
        </RadioGroup>
        {config.multiplex && (
          <Select value={config.multiplex_level} onValueChange={(val) => setConfig({...config, multiplex_level: val})}>
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Select multiplex level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2plex">2plex</SelectItem>
              <SelectItem value="3plex">3plex</SelectItem>
              <SelectItem value="4plex">4plex</SelectItem>
              <SelectItem value="5plex">5plex</SelectItem>
              {technology === 'digital_pcr' && <SelectItem value="6plex">6plex</SelectItem>}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="positive_control"
          checked={config.positive_control}
          onCheckedChange={(checked) => setConfig({...config, positive_control: checked})}
          data-testid="positive-control-checkbox"
        />
        <label htmlFor="positive_control">Add Positive Control (+€100 per pathogen)</label>
      </div>

      {technology === 'realtime_pcr' && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="quant_control"
            checked={config.quantification_control}
            onCheckedChange={(checked) => setConfig({...config, quantification_control: checked})}
            data-testid="quantification-control-checkbox"
          />
          <label htmlFor="quant_control">Add Standard Control for Quantification (+€100 per pathogen)</label>
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>Pathogen Information</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add Pathogen
          </Button>
        </div>
        {config.items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-gray-50 rounded">
            <Input
              placeholder="Scientific name"
              value={item.name}
              onChange={(e) => updateItem(idx, 'name', e.target.value)}
              className="col-span-5"
              data-testid={`pathogen-name-${idx}`}
            />
            <Input
              placeholder="Synonyms/Comment"
              value={item.comment}
              onChange={(e) => updateItem(idx, 'comment', e.target.value)}
              className="col-span-6"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              className="col-span-1"
              disabled={config.items.length === 1}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div>
        <Label>Or Upload FASTA File</Label>
        <Input type="file" accept=".fasta,.fa,.txt,.doc,.docx,.xls,.xlsx" className="mt-1" />
      </div>
    </div>
    );
  };

  const resetGenotypeConfig = (type) => {
    setConfig({
      instrument: '',
      assay_type: '',
      multiplex: false,
      multiplex_level: '',
      species: '',
      exon_spanning: false,
      positive_control: false,
      quantification_control: false,
      synthetic_control: false,
      reference_gene: '',
      items: [{ name: '', sequence: '', comment: '' }],
      genotype_type: type
    });
    setCalculatedPrice(0);
  };

  const renderGenotypingForm = () => {
    if (!config.genotype_type) {
      const infoText = APP_INFO.realtime_pcr?.genotyping;
      
      return (
        <div className="space-y-4">
          {/* Info Alert */}
          {infoText && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
                {infoText}
              </AlertDescription>
            </Alert>
          )}
          
          <Label>Select Genotyping Assay Type</Label>
          <div className="grid gap-4">
            <button
              onClick={() => resetGenotypeConfig('taqman')}
              className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 text-left"
              data-testid="genotype-taqman"
            >
              <h4 className="font-semibold">Hydrolysis ZIP Probe (Taqman)</h4>
              <p className="text-sm text-gray-600 mt-1">Endpoint analysis with FAM/HEX probes</p>
            </button>
            {technology === 'realtime_pcr' && (
              <>
                <button
                  onClick={() => resetGenotypeConfig('fret')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 text-left"
                  data-testid="genotype-fret"
                >
                  <h4 className="font-semibold">FRET LC Probes (Hybridization Probes)</h4>
                  <p className="text-sm text-gray-600 mt-1">Melting analysis with anchor and mutation probes</p>
                </button>
                <button
                  onClick={() => resetGenotypeConfig('simplemt')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 text-left"
                  data-testid="genotype-simplemt"
                >
                  <h4 className="font-semibold">SimpleMT Probe</h4>
                  <p className="text-sm text-gray-600 mt-1">Single probe melting analysis</p>
                </button>
                <button
                  onClick={() => resetGenotypeConfig('gace')}
                  className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 text-left"
                  data-testid="genotype-gace"
                >
                  <h4 className="font-semibold">GACE Mutation Assay</h4>
                  <p className="text-sm text-gray-600 mt-1">Allelic Competitive Extension (requires GACE Master Mix)</p>
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    const seqLength = config.genotype_type === 'taqman' ? '75-1000' : '150-1000';
    
    // Get info text based on genotype type
    let infoText = '';
    if (config.genotype_type === 'taqman') {
      infoText = APP_INFO.realtime_pcr?.genotyping_taqman || APP_INFO.digital_pcr?.genotyping || '';
    } else if (config.genotype_type === 'fret') {
      infoText = APP_INFO.realtime_pcr?.genotyping_fret || '';
    } else if (config.genotype_type === 'simplemt') {
      infoText = APP_INFO.realtime_pcr?.genotyping_simplemt || '';
    } else if (config.genotype_type === 'gace') {
      infoText = APP_INFO.realtime_pcr?.genotyping_gace || '';
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Configure {config.genotype_type.toUpperCase()} Assay</h3>
          <Button variant="outline" size="sm" onClick={() => resetGenotypeConfig('')}>
            Change Type
          </Button>
        </div>

        {/* Info Alert */}
        {infoText && (
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
              {infoText}
            </AlertDescription>
          </Alert>
        )}

        {/* Design Service Only Checkbox */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="design-service-genotype"
              checked={designServiceOnly}
              onCheckedChange={setDesignServiceOnly}
            />
            <div>
              <label htmlFor="design-service-genotype" className="font-medium text-gray-900 cursor-pointer">
                I want to purchase the Design Service only
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Only the design will be completed and shared with you. Each design will be billed at €150.
              </p>
            </div>
          </div>
        </div>

        <div>
          <Label>Instrument</Label>
          <Select value={config.instrument} onValueChange={(val) => setConfig({...config, instrument: val})}>
            <SelectTrigger data-testid="instrument-select">
              <SelectValue placeholder="Choose your instrument" />
            </SelectTrigger>
            <SelectContent>
              {(technology === 'realtime_pcr' ? INSTRUMENTS_QPCR : INSTRUMENTS_DPCR).map(inst => (
                <SelectItem key={inst} value={inst}>{inst}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(config.genotype_type === 'fret') && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="multiplex_genotype"
              checked={config.multiplex}
              onCheckedChange={(checked) => setConfig({...config, multiplex: checked})}
            />
            <label htmlFor="multiplex_genotype">I want multiplex</label>
          </div>
        )}

        <div>
          <Label>Species</Label>
          <Select value={config.species} onValueChange={(val) => setConfig({...config, species: val})}>
            <SelectTrigger data-testid="species-select">
              <SelectValue placeholder="Select species" />
            </SelectTrigger>
            <SelectContent>
              {SPECIES.map(sp => (
                <SelectItem key={sp} value={sp.toLowerCase()}>{sp}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {['fret', 'simplemt', 'gace'].includes(config.genotype_type) && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="synthetic_control"
              checked={config.synthetic_control}
              onCheckedChange={(checked) => setConfig({...config, synthetic_control: checked})}
              data-testid="synthetic-control-checkbox"
            />
            <label htmlFor="synthetic_control">Add Synthetic Mutant Control (+€100 per mutation)</label>
          </div>
        )}

        {config.genotype_type === 'gace' && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">This assay requires GACE Probes Master Mix</p>
            <a href="#" className="text-blue-600 hover:underline text-sm">Purchase GACE Master Mix: 500 rxn (€1,000) | 5000 rxn (€4,500)</a>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Mutation Information</Label>
            <Button type="button" size="sm" variant="outline" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" /> Add Mutation
            </Button>
          </div>
          <p className="text-sm text-gray-600 mb-2">Sequence: {seqLength} nucleotides, 5' to 3', A/C/G/T only</p>
          {config.items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-gray-50 rounded">
              <Input
                placeholder="Mutation region name"
                value={item.name}
                onChange={(e) => updateItem(idx, 'name', e.target.value)}
                className="col-span-4"
                data-testid={`mutation-name-${idx}`}
              />
              <Input
                placeholder="Sequence with mutation (A/T)"
                value={item.sequence}
                onChange={(e) => updateItem(idx, 'sequence', e.target.value)}
                className="col-span-7"
                data-testid={`mutation-sequence-${idx}`}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeItem(idx)}
                className="col-span-1"
                disabled={config.items.length === 1}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Excel Template Upload Section */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet className="w-5 h-5 text-orange-600" />
            <Label className="text-base font-semibold text-orange-800">Or Upload Excel File</Label>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <div className="flex-1 flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="flex-1"
                data-testid="excel-upload-genotyping"
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleAddUploadedData}
                disabled={!uploadedFile}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="add-uploaded-mutation-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add Uploaded Data
              </Button>
            </div>
          </div>
          
          {uploadedFile && (
            <p className="text-sm text-orange-600 mt-2">
              File selected: {uploadedFile.name}
            </p>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            Download the template, fill in your mutation names and sequences, then upload it here.
          </p>
        </div>
      </div>
    );
  };

  const renderGACEMASForm = () => {
    const infoText = APP_INFO.realtime_pcr?.gace_mas;
    
    return (
    <div className="space-y-6">
      {/* Info Alert */}
      {infoText && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
            {infoText}
          </AlertDescription>
        </Alert>
      )}

      {/* Design Service Only Checkbox */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="design-service-gace"
            checked={designServiceOnly}
            onCheckedChange={setDesignServiceOnly}
          />
          <div>
            <label htmlFor="design-service-gace" className="font-medium text-gray-900 cursor-pointer">
              I want to purchase the Design Service only
            </label>
            <p className="text-sm text-gray-600 mt-1">
              Only the design will be completed and shared with you. Each design will be billed at €150.
            </p>
          </div>
        </div>
      </div>

      <div>
        <Label>Instrument</Label>
        <Select value={config.instrument} onValueChange={(val) => setConfig({...config, instrument: val})}>
          <SelectTrigger data-testid="instrument-select">
            <SelectValue placeholder="Choose your instrument" />
          </SelectTrigger>
          <SelectContent>
            {INSTRUMENTS_QPCR.map(inst => (
              <SelectItem key={inst} value={inst}>{inst}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-gray-700 mb-2">This assay requires GACE Probes Master Mix</p>
        <a href="#" className="text-blue-600 hover:underline text-sm">Purchase GACE Master Mix: 500 rxn (€1,000) | 5000 rxn (€4,500)</a>
      </div>

      <div>
        <Label>Species</Label>
        <Select value={config.species} onValueChange={(val) => setConfig({...config, species: val})}>
          <SelectTrigger data-testid="species-select">
            <SelectValue placeholder="Select species" />
          </SelectTrigger>
          <SelectContent>
            {SPECIES.map(sp => (
              <SelectItem key={sp} value={sp.toLowerCase()}>{sp}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="gace_mas_synthetic_control"
          checked={config.synthetic_control}
          onCheckedChange={(checked) => setConfig({...config, synthetic_control: checked})}
          data-testid="gace-mas-synthetic-control-checkbox"
        />
        <label htmlFor="gace_mas_synthetic_control">Add Synthetic Mutant Control (+€100 per mutation)</label>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>MAS Marker Information</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add Marker
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-2">Sequence: 150-1000 nucleotides, 5' to 3', A/C/G/T only</p>
        {config.items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-gray-50 rounded">
            <Input
              placeholder="Marker region name"
              value={item.name}
              onChange={(e) => updateItem(idx, 'name', e.target.value)}
              className="col-span-4"
              data-testid={`marker-name-${idx}`}
            />
            <Input
              placeholder="Sequence with mutation (A/T)"
              value={item.sequence}
              onChange={(e) => updateItem(idx, 'sequence', e.target.value)}
              className="col-span-7"
              data-testid={`marker-sequence-${idx}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              className="col-span-1"
              disabled={config.items.length === 1}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div>
        <Label>Or Upload FASTA File</Label>
        <Input type="file" accept=".fasta,.fa,.txt,.doc,.docx,.xls,.xlsx" className="mt-1" />
      </div>
    </div>
    );
  };

  const renderMiRNAForm = () => {
    const infoKey = technology === 'realtime_pcr' ? 'realtime_pcr' : 'digital_pcr';
    const infoText = APP_INFO[infoKey]?.mirna;
    
    return (
    <div className="space-y-6">
      {/* Info Alert */}
      {infoText && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-gray-700 whitespace-pre-line ml-6">
            {infoText}
          </AlertDescription>
        </Alert>
      )}

      {/* Design Service Only Checkbox */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="design-service-mirna"
            checked={designServiceOnly}
            onCheckedChange={setDesignServiceOnly}
          />
          <div>
            <label htmlFor="design-service-mirna" className="font-medium text-gray-900 cursor-pointer">
              I want to purchase the Design Service only
            </label>
            <p className="text-sm text-gray-600 mt-1">
              Only the design will be completed and shared with you. Each design will be billed at €150.
            </p>
          </div>
        </div>
      </div>

      <div>
        <Label>Species</Label>
        <Select value={config.species} onValueChange={(val) => setConfig({...config, species: val})}>
          <SelectTrigger data-testid="species-select">
            <SelectValue placeholder="Select species" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="human">Human</SelectItem>
            <SelectItem value="mouse">Mouse</SelectItem>
            <SelectItem value="rat">Rat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {technology === 'realtime_pcr' && (
        <div>
          <Label>Assay Type</Label>
          <div className="flex gap-4 mt-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="mirna_sybr"
                checked={config.assay_type === 'sybr_green'}
                onCheckedChange={(checked) => setConfig({...config, assay_type: checked ? 'sybr_green' : ''})}
                data-testid="mirna-sybr-checkbox"
              />
              <label htmlFor="mirna_sybr">SYBR Green</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="mirna_hydrolysis"
                checked={config.assay_type === 'hydrolysis_probe'}
                onCheckedChange={(checked) => setConfig({...config, assay_type: checked ? 'hydrolysis_probe' : ''})}
                data-testid="mirna-hydrolysis-checkbox"
              />
              <label htmlFor="mirna_hydrolysis">Hydrolysis Probe</label>
            </div>
          </div>
        </div>
      )}

      {/* Reference Gene for both Real-Time and Digital PCR */}
      <div>
        <Label>Reference Gene (miRNA Control)</Label>
        <Select value={config.reference_gene || 'none'} onValueChange={(val) => setConfig({...config, reference_gene: val === 'none' ? '' : val})}>
          <SelectTrigger data-testid="reference-gene-select">
            <SelectValue placeholder="Select reference gene (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {MIRNA_CONTROLS.map(ctrl => (
              <SelectItem key={ctrl} value={ctrl}>{ctrl}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {technology === 'digital_pcr' && (
        <>
          <div>
            <Label>Instrument</Label>
            <Select value={config.instrument} onValueChange={(val) => setConfig({...config, instrument: val})}>
              <SelectTrigger data-testid="instrument-select">
                <SelectValue placeholder="Choose your instrument" />
              </SelectTrigger>
              <SelectContent>
                {INSTRUMENTS_DPCR.map(inst => (
                  <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Multiplex</Label>
            <RadioGroup value={config.multiplex ? 'yes' : 'no'} onValueChange={(val) => setConfig({...config, multiplex: val === 'yes'})}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="singleplex" />
                <Label htmlFor="singleplex">SinglePlex</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="multiplex" />
                <Label htmlFor="multiplex">Multiplex</Label>
              </div>
            </RadioGroup>
            {config.multiplex && (
              <Select value={config.multiplex_level} onValueChange={(val) => setConfig({...config, multiplex_level: val})}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select multiplex level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2plex">2plex</SelectItem>
                  <SelectItem value="3plex">3plex</SelectItem>
                  <SelectItem value="4plex">4plex</SelectItem>
                  <SelectItem value="5plex">5plex</SelectItem>
                  <SelectItem value="6plex">6plex</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </>
      )}

      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>miRNA Information</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add miRNA
          </Button>
        </div>
        {config.items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 mb-2 p-3 bg-gray-50 rounded">
            <Input
              placeholder="miRNA name"
              value={item.name}
              onChange={(e) => updateItem(idx, 'name', e.target.value)}
              className="col-span-4"
              data-testid={`mirna-name-${idx}`}
            />
            <Input
              placeholder="Sequence"
              value={item.sequence}
              onChange={(e) => updateItem(idx, 'sequence', e.target.value)}
              className="col-span-7"
              data-testid={`mirna-sequence-${idx}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              className="col-span-1"
              disabled={config.items.length === 1}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Excel Template Upload Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-5 h-5 text-purple-600" />
          <Label className="text-base font-semibold text-purple-800">Or Upload Excel File</Label>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            className="border-purple-300 text-purple-700 hover:bg-purple-100"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          
          <div className="flex-1 flex gap-2">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="flex-1"
              data-testid="excel-upload-mirna"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleAddUploadedData}
              disabled={!uploadedFile}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-uploaded-mirna-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Uploaded Data
            </Button>
          </div>
        </div>
        
        {uploadedFile && (
          <p className="text-sm text-purple-600 mt-2">
            File selected: {uploadedFile.name}
          </p>
        )}
        
        <p className="text-xs text-gray-500 mt-2">
          Download the template, fill in your miRNA names and sequences, then upload it here.
        </p>
      </div>
    </div>
    );
  };

  const renderConfigurationForm = () => {
    if (application === 'gene_expression') return renderGeneExpressionForm();
    if (application === 'pathogen_detection') return renderPathogenDetectionForm();
    if (application === 'genotyping') return renderGenotypingForm();
    if (application === 'mirna') return renderMiRNAForm();
    if (application === 'gace_mas') return renderGACEMASForm();
    if (application === 'custom_probes') {
      // Navigate to integrated custom probes page
      navigate('/custom-probes-primers');
      return null;
    }
    if (application === 'probe_ready') {
      // Navigate to integrated UPL design page
      navigate('/probe-ready-design');
      return null;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <Button onClick={() => navigate('/cart')} variant="outline" data-testid="go-to-cart">
              <ShoppingCart className="w-5 h-5 mr-2" />
              Go to Cart
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center ${step === 'technology' ? 'text-blue-600' : technology ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step === 'technology' ? 'border-blue-600 bg-blue-50' : 
                technology ? 'border-green-600 bg-green-50' : 'border-gray-300'
              }`}>
                1
              </div>
              <span className="ml-2 font-medium">Technology</span>
            </div>
            <div className="h-px w-16 bg-gray-300"></div>
            <div className={`flex items-center ${step === 'application' ? 'text-blue-600' : application ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step === 'application' ? 'border-blue-600 bg-blue-50' : 
                application ? 'border-green-600 bg-green-50' : 'border-gray-300'
              }`}>
                2
              </div>
              <span className="ml-2 font-medium">Application</span>
            </div>
            <div className="h-px w-16 bg-gray-300"></div>
            <div className={`flex items-center ${step === 'configuration' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step === 'configuration' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
              }`}>
                3
              </div>
              <span className="ml-2 font-medium">Configuration</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          {step === 'technology' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Choose Your Technology</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => handleTechnologySelect('realtime_pcr')}
                  className="p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all text-left"
                  data-testid="tech-realtime-pcr"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time PCR</h3>
                  <p className="text-gray-600">Quantitative PCR for gene expression, genotyping, and more</p>
                </button>
                <button
                  onClick={() => handleTechnologySelect('digital_pcr')}
                  className="p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all text-left"
                  data-testid="tech-digital-pcr"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Digital PCR</h3>
                  <p className="text-gray-600">Absolute quantification with superior precision</p>
                </button>
              </div>
            </div>
          )}

          {step === 'application' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Select Application Type</h2>
                <Button variant="outline" onClick={() => setStep('technology')}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Change Technology
                </Button>
              </div>
              {renderApplicationButtons()}
            </div>
          )}

          {step === 'configuration' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Configure Your Assay</h2>
                <Button variant="outline" onClick={() => { setStep('application'); setApplication(''); }}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Change Application
                </Button>
              </div>
              
              {renderConfigurationForm()}

              {!['custom_probes', 'probe_ready'].includes(application) && (
                <div className="border-t pt-6 mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Button
                        onClick={handleCalculate}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="calculate-button"
                      >
                        Calculate Price
                      </Button>
                    </div>
                    {calculated_price > 0 && (
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Estimated Price</p>
                        <p className="text-3xl font-bold text-blue-600" data-testid="calculated-price">
                          €{calculated_price.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {calculated_price > 0 && (
                    <div className="flex gap-4">
                      <Button
                        onClick={handleAddToCart}
                        variant="outline"
                        className="flex-1"
                        data-testid="add-to-cart-button"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                      <Button
                        onClick={handleOrderNow}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        data-testid="order-now-button"
                      >
                        Order Now
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WorkflowPage;
